import type { LsgReportId, LSGRunVisibility } from '@brightlocal/shared';

import type { LSGCreateReportSettings, LSGUpdateReportSettings } from './schema';
import {
  LSGCreateReportResponseSchema,
  LSGRunReportResponseSchema,
  LSGReportSchema,
  LSGCompetitorsResponseSchema,
  LSGCompetitorRankingsSchema,
  LSGReportRunSchema,
  LSGReportRunsResponseSchema,
  LSGGridPointsResponseSchema
} from './schema';
import { HTTP_METHOD } from '../../../consts';
import { MessageResponseSchema } from '../../../schemas';
import { serializeRequestBody } from '../../../serializer';
import type { ApiCallOptions, Transport } from '../../../types';

export interface LSGGetReportRunsParams {
  /** Number of results per page; omitted query param when undefined. */
  numPerPage?: number;
  /** Restrict runs to the active or archived set; forwarded as `filter` query param when set. */
  visibility?: LSGRunVisibility;
}

export const createLSGReportApi = (transport: Transport) => ({
  createReport: (settings: LSGCreateReportSettings, options?: ApiCallOptions) =>
    transport(`/manage/v1/lsg/reports`, {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(settings),
      deserializer: LSGCreateReportResponseSchema,
      signal: options?.signal
    }),
  runReport: (reportId: number, options?: ApiCallOptions) =>
    transport(`/manage/v1/lsg/reports/${reportId}/actions/run`, {
      method: HTTP_METHOD.POST,
      deserializer: LSGRunReportResponseSchema,
      signal: options?.signal
    }),
  getReport: (reportId: number, options?: ApiCallOptions) =>
    transport(`/manage/v1/lsg/reports/${reportId}`, {
      method: HTTP_METHOD.GET,
      deserializer: LSGReportSchema,
      signal: options?.signal
    }),
  /** Partial update: only the fields in `settings` change. */
  updateReport: (reportId: LsgReportId, settings: LSGUpdateReportSettings, options?: ApiCallOptions) =>
    transport(`/manage/v1/lsg/reports/${reportId}`, {
      method: HTTP_METHOD.PATCH,
      body: serializeRequestBody(settings),
      deserializer: MessageResponseSchema,
      signal: options?.signal
    }),
  getLatestRunForKeyword: (reportId: number, keywordId: number, options?: ApiCallOptions) =>
    transport(`/manage/v1/lsg/reports/${reportId}/runs/latest/keywords/${keywordId}`, {
      method: HTTP_METHOD.GET,
      deserializer: LSGReportRunSchema,
      signal: options?.signal
    }),
  getReportRuns: (reportId: number, keywordId: number, params?: LSGGetReportRunsParams, options?: ApiCallOptions) =>
    transport(`/manage/v1/lsg/reports/${reportId}/keywords/${keywordId}/runs`, {
      method: HTTP_METHOD.GET,
      deserializer: LSGReportRunsResponseSchema,
      signal: options?.signal,
      // transport skips undefined/null keys, so unset params are omitted from the URL automatically
      queryParams: params ? { num_per_page: params.numPerPage, filter: params.visibility } : undefined
    }),
  getCompetitors: (reportId: number, runId: number, keywordId: number, options?: ApiCallOptions) =>
    transport(`/manage/v1/lsg/reports/${reportId}/runs/${runId}/keywords/${keywordId}/competitors`, {
      method: HTTP_METHOD.GET,
      deserializer: LSGCompetitorsResponseSchema,
      signal: options?.signal
    }),
  getCompetitorRankings: (reportId: number, runId: number, keywordId: number, cid: string, options?: ApiCallOptions) =>
    transport(`/manage/v1/lsg/reports/${reportId}/runs/${runId}/keywords/${keywordId}/competitors/${cid}`, {
      method: HTTP_METHOD.GET,
      deserializer: LSGCompetitorRankingsSchema,
      signal: options?.signal
    }),
  getGridPoints: (reportId: number, runId: number, keywordId: number, pointId: string, options?: ApiCallOptions) =>
    transport(`/manage/v1/lsg/reports/${reportId}/runs/${runId}/keywords/${keywordId}/points/${pointId}`, {
      method: HTTP_METHOD.GET,
      deserializer: LSGGridPointsResponseSchema,
      signal: options?.signal
    })
});

export type LSGReportsApi = ReturnType<typeof createLSGReportApi>;
